import './styles.css';
import {ErrorMessage, Formik} from 'formik';
import clone from 'lodash/clone';
import React, {useEffect, useState} from 'react';
import MediaQuery from 'react-responsive';
import {CSSTransition} from 'react-transition-group';
import Avatar from '../../../../components/Avatar';
import BasicButton from '../../../../components/BasicButton';
import BasicButtonGroup from '../../../../components/BasicButtonGroup';
import BasicCheckbox from '../../../../components/BasicCheckbox';
import BasicLabel from '../../../../components/BasicLabel';
import BasicSelect from '../../../../components/BasicSelect';
import BasicSubmitButton from '../../../../components/BasicSubmitButton';
import HorizontalLoader from '../../../../components/HorizontalLoader';
import LoadMask from '../../../../components/LoadMask';
import TextEditor from '../../../../components/TextEditor';
import {ANIMATION_TIMEOUT_APPEAR} from '../../../../constants/animation';
import ENDPOINT from '../../../../constants/endpoints';
import {STUDENT_ROLE_ID} from '../../../../constants/roles';
import {convertEditorStateToHtml, convertHtmlToEditorState} from '../../../../helpers/formik';
import useApi from '../../../../hooks/useApi';
import {CandidateSetRatingFormValidationSchema} from './validate';

const CandidateSetRatingForm = props => {
  const {
    params: {
      disableForm,
      candidate: {
        CandidateID,
        FullName,
        PictureUrl,
        FirmName,
        SchoolName,
        OfficeLocationName,
        RoleID,
        currentSchoolName,
      },
      processId,
    },
    actions: { proceed, dismiss, handleError },
  } = props;

  const api = useApi();
  const [state, setState] = useState({
    init: false,
    fields: {
      Rating: 0,
      Met: false,
      Spoke: false,
      Notes: null,
    },
    options: {},
  });

  const [editorState, setEditorState] = useState(convertHtmlToEditorState(''));

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.RATE_PROFILE,
      {
        id: CandidateID,
      },
      {
        handleSuccess: data => {
          setEditorState(convertHtmlToEditorState(data.fields.Notes));
          setState({
            ...state,
            init: true,
            ...data,
            fields: {
              ...data.fields,
              Rating: data.options.Rating.find(
                rating => rating.value === data.fields.Rating
              ),
            },
          })
        },
      }
    );
  }, []);

  const handleSubmit = (values, { setSubmitting, setErrors }) => {
    const preparedValues = clone(values);

    preparedValues.Notes = convertEditorStateToHtml(editorState);

    Object.keys(preparedValues).forEach(field => {
      let data = preparedValues[field];
      if (typeof data === 'object') {
        preparedValues[field] = data.value;
      } else if (typeof data === 'boolean') {
        preparedValues[field] = data ? 1 : 0;
      } else if (processId) {
        preparedValues.processId = processId;
      }
    });

    api.post(
      ENDPOINT.FORM.SAVE.RATE_PROFILE,
      {
        id: CandidateID,
        ...preparedValues,
      },
      {
        handleForbidden: () => {
          setSubmitting(false);
          handleError({
            data: {
              message: 'You do not have permission to rate candidates.',
            },
          });
        },
        handleError: response => {
          setSubmitting(false);
          handleError(response, setErrors);
        },
        handleSuccess: () =>
          proceed({
            Rating: preparedValues.Rating,
            Spoke: preparedValues.Spoke,
            Met: preparedValues.Met,
            Notes: preparedValues.Notes,
          }),
      }
    );
  };

  const isStudent = Number(RoleID) === STUDENT_ROLE_ID;

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={state.fields}
          validationSchema={CandidateSetRatingFormValidationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form
              onSubmit={handleSubmit}
              className="form__content candidate-set-rating"
            >
              {isSubmitting && <LoadMask />}

              <div className="candidate-set-rating__container">
                <div className="candidate-set-rating__avatar">
                  <Avatar size={55} name={FullName} image={PictureUrl} />
                </div>

                <div className="candidate-set-rating__content">
                  <div className="candidate-set-rating__row">
                    <div className="candidate-set-rating__person">
                      <span className="candidate-set-rating__title">
                        {FullName}
                      </span>
                    </div>
                  </div>
                  <div className="candidate-set-rating__row">
                    <div className="candidate-set-rating__info">
                      <span className="candidate-set-rating__company">
                        {isStudent ? currentSchoolName : FirmName}
                      </span>
                      {!isStudent && SchoolName && (
                        <span className="candidate-set-rating__school">
                          {SchoolName}
                        </span>
                      )}
                      <span className="candidate-set-rating__location">
                        {OfficeLocationName}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="form__group form__group--compact"
                style={{
                  marginBottom: '15px',
                }}
              >
                <div className="form__row form__row--compact">
                  <MediaQuery query="(max-width: 480px)">
                    <div className="candidate-set-rating__label">
                      <p className="candidate-set-rating__message">
                        Rate from 1 to 10 this candidate.
                      </p>
                    </div>
                  </MediaQuery>
                  <div className="candidate-set-rating__rating">
                    <BasicSelect
                      name="Rating"
                      value={values.Rating}
                      isValid={
                        (!errors.Rating && !!touched.Rating) || !touched.Rating
                      }
                      isDisabled={disableForm}
                      isSearchable={false}
                      placeholder="Select From List"
                      options={state.options.Rating}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                    />
                    <ErrorMessage
                      name="Rating"
                      render={message => (
                        <div className="form__error">{message}</div>
                      )}
                    />
                  </div>
                  <MediaQuery query="(min-width: 481px)">
                    <div className="candidate-set-rating__label">
                      <p className="candidate-set-rating__message">
                        Rate from 1 to 10 this candidate.
                      </p>
                    </div>
                  </MediaQuery>
                </div>
              </div>

              <div
                className="form__group form__group--compact"
                style={{
                  marginBottom: '15px',
                }}
              >
                <BasicCheckbox
                  value={values.Spoke}
                  singleRow
                  disabled={disableForm}
                  checked={values.Spoke}
                  title="Spoke to Candidate"
                  name="Spoke"
                  controlFunc={handleChange}
                  labelStyles={{
                    fontFamily: 'Noto Sans, sans-serif',
                  }}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{
                  marginBottom: '30px',
                }}
              >
                <BasicCheckbox
                  disabled={disableForm}
                  value={values.Met}
                  singleRow
                  checked={values.Met}
                  title="Met Candidate"
                  name="Met"
                  controlFunc={handleChange}
                  labelStyles={{
                    fontFamily: 'Noto Sans, sans-serif',
                  }}
                />
              </div>

              <div
                className="form__group form__group--compact"
                style={{
                  marginBottom: '5px',
                }}
              >
                <BasicLabel
                  name="Notes"
                  required={false}
                  text="Notes"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />

                <TextEditor
                  disabled={disableForm}
                  name={'Notes'}
                  enableHyperlinks={false}
                  editorState={editorState}
                  controlFunc={(name, state) => {
                    setEditorState(state);
                  }}
                  touchFunc={handleBlur}
                  isValid={(!errors.Notes && !!touched.Notes) || !touched.Notes}
                />

                <ErrorMessage
                  name="Notes"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup>
                  <BasicSubmitButton
                    disabled={disableForm}
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={700}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Save Rating"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={700}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <HorizontalLoader />
      </div>
    </div>
  );
};

export default CandidateSetRatingForm;
